import React,{ forwardRef, useImperativeHandle, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { push } from 'redux-first-history'
import { confirmPopup } from 'primereact/confirmpopup'
import { confirmDialog } from 'primereact/confirmdialog'
import { SplitButton } from 'primereact/splitbutton'
import { validateEpisodeDetails, validateContributionPercentages, getContributionValidationDialogConfig, showContributionValidationDialog, showContributionDeletionConfirmation, showContributionChangeConfirmation, validateContributionZeroValues, validateNonEligibleContributorTypes, hasOnlyNonEligibleContributionChanges, validateDuplicateContributions } from 'components/Body/data-entry/AudioVisual/validations'
import { selectProgrammeGuid, selectProgrammeUrl, selectEpisodeDraftHasDeletedTransmissions, selectEpisodeDraftHasDeletedContributions, selectEpisodeResultHasChanges, selectEpisodeDraftHasContributions, selectIsContributionRowEditing, selectEpisodeResultOriginal } from 'components/Body/data-entry/AudioVisual/store/selectors'
import { setIsNewEpisodeAction, setEpisodeDataUpdateKeyAction, setShowEpisodeDataUpdateAction, setShowEpisodeDataCreateAction, setEpisodeUpdateStatusAction, clearProgrammeDraftResultsEpisodesAction, setEpisodeSaveAndExitClickedAction, clearEpisodeResultsAction, setEpisodeGuidAction } from 'components/Body/data-entry/AudioVisual/store/actions'

// ReSharper disable once InconsistentNaming
export const EpisodeDetailsFooter = forwardRef(({ 
    cancelConfirmRef, 
    episodeData, 
    programmeData, 
    episodePrimaryTitle, 
    toast, 
    isNewEpisode
}) => {
    const timeoutIds = useRef([])
    const dispatch = useDispatch()
    const draftAvailable = useSelector(selectEpisodeResultHasChanges)
    const deleteTransmissionAvailable = useSelector(selectEpisodeDraftHasDeletedTransmissions)
    const deleteContributionAvailable = useSelector(selectEpisodeDraftHasDeletedContributions)
    const hasContributionChanges = useSelector(selectEpisodeDraftHasContributions)
    const isContributionEditing = useSelector(selectIsContributionRowEditing)
    const programmeUrl = useSelector(selectProgrammeUrl)
    const programmeGuid = useSelector(selectProgrammeGuid)
    const episodeDataOriginal = useSelector(selectEpisodeResultOriginal)
    /**
     * Shows validation error dialog for Non-Eligible Contributors with invalid contribution types.
     * @returns {boolean} - true if validation passed, false if failed
     */
    const validateAndShowNonEligibleError = () => {
        const nonEligibleValidation = validateNonEligibleContributorTypes(episodeData)
        if (!nonEligibleValidation.isValid) {
            confirmDialog({
                message: nonEligibleValidation.message,
                header: 'Validation Error',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'OK',
                acceptClassName: 'p-button-warning',
                rejectVisible: false,
                accept: () => {}
            })
            return false
        }
        return true
    }

    const clearProgrammeEpisodeDraftResults = () => dispatch(clearProgrammeDraftResultsEpisodesAction())
    const setShowDataUpdate = (show) => dispatch(setShowEpisodeDataUpdateAction(show))
    const setShowDataCreate = (show) => dispatch(setShowEpisodeDataCreateAction(show))
    const setDataUpdateKey = (key) => dispatch(setEpisodeDataUpdateKeyAction(key))
    const setIsNewEpisode = (isNew) => dispatch(setIsNewEpisodeAction(isNew))
    const setEpisodeSaveAndExitClicked = (clicked) => dispatch(setEpisodeSaveAndExitClickedAction(clicked))

    const setTrackedTimeout = (fn, delay) => {
        const id = setTimeout(fn, delay)
        timeoutIds.current.push(id)
    }

    const clearTrackedTimeouts = () => {
        timeoutIds.current.forEach(clearTimeout)
        timeoutIds.current.length = 0
    }

    const handleCancelButtonClick = () => {
        clearTrackedTimeouts()
        dispatch(setEpisodeGuidAction(null))
        const baseUrl = window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'))
        dispatch(push(baseUrl))
        setIsNewEpisode(false)
    }

    const handleCreateTrigger = () => {
        dispatch(setEpisodeUpdateStatusAction({ updateStatus: 'idle' }))
        setShowDataCreate(false)
        setDataUpdateKey(Date.now())
        setShowDataCreate(true)
    }

    const handleUpdateTrigger = () => {
        dispatch(setEpisodeUpdateStatusAction({ updateStatus: 'idle' }))
        setShowDataUpdate(false)
        setDataUpdateKey(Date.now())
        setShowDataUpdate(true)
    }

    /**** Cancel Logic in Footer - Begin ****/
    const accept = () => {
        if (!draftAvailable) {
            setTrackedTimeout(() => {
                handleCancelButtonClick()
            }, 1000)
            return
        }
        try {
            clearProgrammeEpisodeDraftResults()
            toast.current.show({
                severity: 'info',
                summary: 'Confirmed',
                detail: 'Pending changes are cancelled',
                life: 3000
            })
            setTrackedTimeout(() => {
                handleCancelButtonClick()
            }, 1000)
        } catch (error) {
            console.error('Error in accept function:', error)
        }
    }

    const showCancelConfirm = (event) => {
        if (draftAvailable) {
            confirmPopup({
                target: event.currentTarget,
                message: 'You have unsaved changes. Do you want to discard them?',
                icon: 'pi pi-info-circle',
                defaultFocus: 'reject',
                acceptClassName: 'p-button-danger',
                accept
            })
        }
        else {
            setTrackedTimeout(() => {
                handleCancelButtonClick()
            }, 1000)
        }
    }

    useImperativeHandle(cancelConfirmRef, () => ({
        showCancelConfirm: (event) => showCancelConfirm(event),
        handleCancelButtonClick: () => handleCancelButtonClick()
    }))
    /**** Cancel Logic in Footer - End ****/

    /**** Save Logic in Footer - Begin ****/

    const handleTransmissionConfirmation = (event, action) => {
        const target = event.target || (event.originalEvent && event.originalEvent.currentTarget)
        confirmPopup({
            target: target,
            message: 'You are about to delete Transmissions. Do you want to continue?',
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            accept: action,
            reject: rejectTransmissionChange
        })
    }

    // Validate contributions and return validation result
    const validateContributions = () => {
        //Validate duplicate contributions and return result           
        const { result: duplicateResult, duplicates } =
            validateDuplicateContributions(episodeData)

        const duplicateDialogConfig =
            getContributionValidationDialogConfig(duplicateResult)

        if (duplicateDialogConfig) {
            return {
                isValid: false,
                dialogConfig: duplicateDialogConfig,
                duplicates
            }
        }

        //Validate contribution percentages and return result and total percentage
        const { result, totalPercentage } = validateContributionPercentages(episodeData, hasContributionChanges, deleteContributionAvailable)
        const dialogConfig = getContributionValidationDialogConfig(result, totalPercentage)

        return {
            isValid: !dialogConfig,
            dialogConfig
        }
    }

    const handleSaveAction = async (event) => {
        if (deleteTransmissionAvailable) {
            handleTransmissionConfirmation(event, () => handleSaveAfterTransmissionConfirm(event))
        } else if (deleteContributionAvailable) {
            showContributionDeletionConfirmation(event, episodeData, episodeDataOriginal?.contributions || {}, acceptSave)
        } else if (hasContributionChanges) {
            // Validate contributions BEFORE showing confirmation dialog
            const contributionValidation = validateContributions()
            if (!contributionValidation.isValid) {
                // Show validation error dialog and block save
                showContributionValidationDialog(contributionValidation.dialogConfig)
                return
            }

            // Check if only Actor/Director contributions changed
            const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                episodeData,
                episodeDataOriginal?.contributions || {}
            )

            if (onlyNonEligibleChanges) {
                // Skip reapportionment confirmation for Actor/Director-only changes
                await performSave()
            } else {
                // Show reapportionment confirmation for eligible contribution changes
                showContributionChangeConfirmation(acceptSave)
            }
        } else {
            await performSave()
        }
    }

    const handleSaveAfterTransmissionConfirm = async (event) => {
        if (deleteContributionAvailable) {
            showContributionDeletionConfirmation(event, episodeData,
                episodeDataOriginal?.contributions || {}, acceptSave)
        } else if (hasContributionChanges) {
            // Validate contributions BEFORE showing confirmation dialog
            const contributionValidation = validateContributions()
            if (!contributionValidation.isValid) {
                // Show validation error dialog and block save
                showContributionValidationDialog(contributionValidation.dialogConfig)
                return
            }

            // Check if only Actor/Director contributions changed
            const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                episodeData,
                episodeDataOriginal?.contributions || {}
            )

            if (onlyNonEligibleChanges) {
                // Skip reapportionment confirmation for Actor/Director-only changes
                await acceptSave()
            } else {
                // Show reapportionment confirmation for eligible contribution changes
                showContributionChangeConfirmation(acceptSave)
            }
        } else {
            await acceptSave()
        }
    }

    const performSave = async () => {
        const isValid = await validateEpisodeDetails(toast, dispatch, episodeData, episodePrimaryTitle, programmeGuid, programmeUrl)
        if (!isValid) {
            return
        }

        // Validate contributions don't have 0 percentage or minutes (except Actor/Director)
        const zeroValueValidation = validateContributionZeroValues(episodeData)
        if (!zeroValueValidation.isValid) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: zeroValueValidation.message })
            return
        }

        // Validate Non-Eligible Contributors have only Actor or Director contribution types
        if (!validateAndShowNonEligibleError()) return

        // Validate contributions
        const contributionValidation = validateContributions()
        if (!contributionValidation.isValid) {
            // Show validation error dialog and block save
            showContributionValidationDialog(contributionValidation.dialogConfig)
            return
        }

        // All validations passed, proceed with save
        // Success dialog will be shown in EpisodeDetailsContent after server response
        isNewEpisode ? handleCreateTrigger() : handleUpdateTrigger()
    }

    //On direct click of Accept in split button
    const splitSave = async (event) => {
        await handleSaveAction(event)
    }

    //Accept Split Buttom Items
    const saveModalItems = [
        {
            label: 'Save',
            icon: 'fa-light fa-check',
            command: async (event) => {
                await handleSaveAction(event)
            }
        },
        {
            label: 'Save and Exit',
            icon: 'fa-light fa-check',
            command: async (event) => {
                if (deleteTransmissionAvailable) {
                    handleTransmissionConfirmation(event, () => handleSaveAndExitAfterTransmissionConfirm(event))
                } else if (deleteContributionAvailable) {
                    showContributionDeletionConfirmation(event, episodeData,
                        episodeDataOriginal?.contributions || {}, acceptSaveAndExit)
                } else if (hasContributionChanges) {
                    // Validate contributions BEFORE showing confirmation dialog
                    const contributionValidation = validateContributions()
                    if (!contributionValidation.isValid) {
                        // Show validation error dialog and block save
                        showContributionValidationDialog(contributionValidation.dialogConfig)
                        return
                    }

                    // Check if only Actor/Director contributions changed
                    const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                        episodeData,
                        episodeDataOriginal?.contributions || {}
                    )

                    if (onlyNonEligibleChanges) {
                        // Skip reapportionment confirmation for Actor/Director-only changes
                        await performSaveAndExit()
                    } else {
                        // Show reapportionment confirmation for eligible contribution changes
                        showContributionChangeConfirmation(acceptSaveAndExit)
                    }
                } else {
                    await performSaveAndExit()
                }
            }
        }
    ]

    const handleSaveAndExitAfterTransmissionConfirm = async (event) => {
        if (deleteContributionAvailable) {
            showContributionDeletionConfirmation(event, episodeData,
                episodeDataOriginal?.contributions || {}, acceptSaveAndExit)
        } else if (hasContributionChanges) {
            // Validate contributions BEFORE showing confirmation dialog
            const contributionValidation = validateContributions()
            if (!contributionValidation.isValid) {
                // Show validation error dialog and block save
                showContributionValidationDialog(contributionValidation.dialogConfig)
                return
            }

            // Check if only Actor/Director contributions changed
            const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                episodeData,
                episodeDataOriginal?.contributions || {}
            )

            if (onlyNonEligibleChanges) {
                // Skip reapportionment confirmation for Actor/Director-only changes
                await acceptSaveAndExit()
            } else {
                // Show reapportionment confirmation for eligible contribution changes
                showContributionChangeConfirmation(acceptSaveAndExit)
            }
        } else {
            await acceptSaveAndExit()
        }
    }

    const performSaveAndExit = async () => {
        const isValid = await validateEpisodeDetails(toast, dispatch, episodeData, episodePrimaryTitle, programmeGuid, programmeUrl)
        if (!isValid) {
            return
        }

        // Validate contributions don't have 0 percentage or minutes (except Actor/Director)
        const zeroValueValidation = validateContributionZeroValues(episodeData)
        if (!zeroValueValidation.isValid) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: zeroValueValidation.message })
            return
        }

        // Validate Non-Eligible Contributors have only Actor or Director contribution types
        if (!validateAndShowNonEligibleError()) return

        // Validate contributions
        const contributionValidation = validateContributions()
        if (!contributionValidation.isValid) {
            showContributionValidationDialog(contributionValidation.dialogConfig)
            return
        }

        // All validations passed, proceed with save
        // Success dialog will be shown in EpisodeDetailsContent after server response
        isNewEpisode ? handleCreateTrigger() : handleUpdateTrigger()
        setEpisodeSaveAndExitClicked(true)
    }

    const acceptSaveAndExit = async () => {
        await performSaveAndExit()
    }

    const acceptSave = async () => {
        await performSave()
    }

    const rejectTransmissionChange = () => {
    }

    /**** Save Logic in Footer - End ****/

    return (
        <div className="app-footer episode-details-footer">
            <SplitButton className='green mr-2 mt-2' label="Save" icon="fa-light fa-check" onClick={splitSave} dropdownIcon="fa-light fa-chevron-down" model={saveModalItems} disabled={(!draftAvailable && !isNewEpisode) || isContributionEditing} />
            <Button label="Cancel" icon="fa-light fa-xmark" className="yellow mt-2 mr-3 " raised onClick={showCancelConfirm} ></Button>
        </div>
    )
})
